<template>
    <div class="container mx-auto px-4">
        <h1 class="font-serif mb-8 md:text-5xl text-2xl text-center">{{ __('Felhasználók') }}</h1>

        <div class="flex items-center mb-8">
            <c-control-text class="w-full" class-field="!h-10 !text-lg" class-group="bg-white border pl-2 rounded-none"
                            icon="search"
                            name="search"
                            v-bind:placeholder="__('Keresés')"
                            v-model="s"
                            v-on:keyup="search"
            />

            <div class="flex items-center ml-4 shrink-0">
                <c-button class="bg-slate-800 rounded-br-none rounded-tr-none shrink-0 text-white"
                          router
                          v-bind:to="{ name : 'users.create' }"
                >
                    + {{ __('Új felhasználó') }}
                </c-button>
                <div class="after:absolute after:border-[5px] after:border-transparent after:border-t-white after:content-[''] after:h-0 after:mt-1 after:left-1/2 after:pointer-events-none after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-0 px-4 bg-slate-800 border-l border-slate-500 cursor-pointer h-9 hover:bg-slate-800 py-2.5 relative rounded-br rounded-tr"
                     v-on:click="showAddMenu = !showAddMenu"
                     v-on-click-outside="closeAddMenu"
                >
                    <nav class="absolute bg-white border border-slate-200 flex flex-col min-w-[150px] mt-2 right-0 rounded shadow-sm text-sm top-full whitespace-nowrap z-10"
                         v-show="showAddMenu"
                    >
                        <router-link class="flex border-b border-slate-200 items-center p-2 text-slate-800"
                                     v-bind:to="{ name : 'users.create' }"
                        >
                            <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="plus" />{{ __('Új felhasználó') }}
                        </router-link>
                        <a class="flex border-b border-slate-200 items-center p-2 text-slate-800"
                           v-on:click="exportXls"
                        >
                            <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="download" />{{ __('Exportálás') }}
                        </a>
                    </nav>
                </div>
            </div>
        </div>

        <c-data-table responsive-breakpoint="lg"
                      v-bind:data="tableData"
                      v-if="tableData.items.length > 0"
                      v-on:sort-table="sortTable"
        >
            <template v-slot:item.email="{ item }">
                <router-link class="break-all hover:decoration-dotted hover:underline"
                             v-bind:to="{ name: 'users.edit', params: { user: item.values.id } }"
                >
                    {{ item.values.email }}
                </router-link>
            </template>
            <template v-slot:item.lastname="{ item }">
                <router-link class="hover:decoration-dotted hover:underline"
                             v-bind:to="{ name: 'users.edit', params: { user: item.values.id } }"
                >
                    {{ item.values.lastname }}
                </router-link>
            </template>
            <template v-slot:item.firstname="{ item }">
                <router-link class="hover:decoration-dotted hover:underline"
                             v-bind:to="{ name: 'users.edit', params: { user: item.values.id } }"
                >
                    {{ item.values.firstname }}
                </router-link>
            </template>
            <template v-slot:item.role="{ item }">
                {{ item.values.role }}
            </template>
            <template v-slot:item.group="{ item }">
                {{ item.values.group?.name ?? '' }}
            </template>
            <template v-slot:item.created_at="{ item }">
                {{ DateTime.fromISO(item.values.created_at).toFormat(localizationStore.dateTimeFormat) }}
            </template>
            <template v-slot:item.last_login_at="{ item }">
                <span v-if="item.values.last_login_at">
                    {{ DateTime.fromISO(item.values.last_login_at).toFormat(localizationStore.dateTimeFormat) }}
                </span>
                <span v-else>-</span>
            </template>
            <template v-slot:item.is_blacklisted="{ item }">
                <div class="flex items-center justify-center">
                    <c-icon class="h-6 stroke-error w-6" icon="check-circle" v-if="item.values.is_blacklisted" />
                    <c-icon class="h-6 stroke-success w-6" icon="x-square" v-else />
                </div>
            </template>
            <template v-slot:item.actions="{ item }">
                <div class="flex flex-col">
                    <router-link class="cursor-pointer flex hover:decoration-dotted hover:underline text-slate-800 text-xs"
                                 v-bind:to="{ name: 'users.edit', params: { user: item.values.id } }"
                    >
                        <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="edit-3" />
                        {{ __('Szerkesztés') }}
                    </router-link>
                    <a class="cursor-pointer flex hover:decoration-dotted hover:underline mt-2 text-error text-xs"
                       v-on:click="deletedModel = item.values.id"
                    >
                        <c-icon class="h-4 mr-2 stroke-error w-4" icon="trash-2" />
                        {{ __('Törlés') }}
                    </a>
                    <router-link class="cursor-pointer flex hover:decoration-dotted hover:underline mt-2 text-slate-800 text-xs"
                                 v-bind:to="{ name: 'login', query: { token: item.values.login_as_token } }"
                    >
                        <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="log-in" />
                        {{ __('Belépés mint :user', { user: item.values.firstname }) }}
                    </router-link>
                </div>
            </template>
        </c-data-table>

        <span class="block font-serif text-center text-2xl" v-else>{{ __('Nincs találat') }}</span>
    </div>

    <c-modal class="md:w-[450px]" v-bind:open="deletedModel !== null" v-bind:title="__('Felhasználó törlése')" v-on:closeModal="deletedModel = null">
        <template v-slot:body>
            <div class="py-5 text-center text-sm">
                {{ __('Biztosan törölni szeretnéd ezt a felhasználót?') }}
            </div>
        </template>
        <template v-slot:footer>
            <div class="flex justify-end w-full">
                <c-button class="bg-slate-100 mr-2 text-slate-800" v-on:click="deletedModel = null">
                    {{ __('Mégsem') }}
                </c-button>

                <c-button class="bg-error text-white" v-on:click="del">
                    {{ __('Törlés') }}
                </c-button>
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
    import { inject, onMounted, ref } from 'vue';
    import { vOnClickOutside } from '@vueuse/components';
    import {
        CButton,
        CControlText,
        CDataTable,
        CIcon,
        CModal,
    } from '@teamfurther/cinderblock';
    import debounce from 'debounce';
    import { DateTime } from 'luxon';
    import UserRepository from '../../repositories/UserRepository';
    import { useLocalizationStore } from '../../stores/localization';

    const __ = inject('__');
    const deletedModel = ref(null);
    const localizationStore = useLocalizationStore();
    const s = ref<string>('');
    const showAddMenu = ref<boolean>(false);
    const sort = ref<string>('lastname');
    const tableData = ref<object>({
        columnAligns: { is_blacklisted: 'center' },
        columnNames: {
            lastname: __('Vezetéknév'),
            firstname: __('Keresztnév'),
            email: __('E-mail cím'),
            role: __('Szerepkör'),
            group: __('Csoport'),
            created_at: __('Regisztráció időpontja'),
            last_login_at: __('Utolsó belépés időpontja'),
            is_blacklisted: __('Feketelistás?'),
            actions: __('Műveletek'),
        },
        defaultSort: '+lastname',
        items: [],
        sortableColumns: ['created_at', 'email', 'firstname', 'group', 'is_blacklisted', 'last_login_at', 'lastname', 'role'],
    });
    const userRepository = new UserRepository();

    function closeAddMenu() {
        showAddMenu.value = false;
    }

    async function del() {
        await userRepository.destroy(deletedModel.value);

        await getUsers();

        deletedModel.value = null;
    }

    async function getUsers() {
        const result = await userRepository.index({
            filters: {
                search : s.value,
            },
            include: ['group'],
            sort: sort.value,
        });

        tableData.value.items = result.map((user) => {
            return { values : user }
        });
    }

    async function exportXls() {
        await userRepository.export();
    }

    const search = debounce(async () => {
        await getUsers();
    }, 500);

    async function sortTable(sortBy: string) {
        sort.value = sortBy;

        await getUsers();
    }

    onMounted(async () => {
        await getUsers();
    });
</script>
